import {
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { SchoolEventType } from '../../../pages/Substitutions/Plan/PlanSchoolEvent/PlanSchoolEvent';
import {
  use_CreateRecurringSchoolEventMutation,
  use_CreateSingleSchoolEventMutation,
  use_EventsQuery,
} from '../../../types/planung-graphql-client-defs';
import {
  combineFirstDateWithSecondTime,
  getDaysDifference,
  setTimeStringOnDate,
} from '../../../utils/dateCalculations';
import { lowercaseFirstLetter } from '../../../utils/stringHelper';
import { showSuccessCreateToast, showUserErrorToast } from '../../../utils/toast';
import { RecurringEventForm } from './RecurringEventForm';
import { schoolEventSchema } from './schema';

export type SchoolEventFormProps = {
  eventUuid?: string | null;
  onLoading: (loading: boolean) => void;
  onClose: () => void;
};

export const SchoolEventForm = ({ eventUuid, onLoading, onClose }: SchoolEventFormProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;

  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const schoolEventContext = useMemorizedCacheTag('SCHOOL_EVENT');

  const [{ data: eventData }] = use_EventsQuery({
    variables: {
      where: {
        uuid: eventUuid,
      },
    },
    context: schoolEventContext,
  });

  const [, createSingleEvent] = use_CreateSingleSchoolEventMutation();
  const [, createRecurringEvent] = use_CreateRecurringSchoolEventMutation();

  const currentEvent = eventData?.events[0];

  const initialValues: SchoolEventType = useMemo(() => {
    return {
      uuid: currentEvent?.uuid ?? '',
      name: currentEvent?.comment ?? '',
      classUuids:
        currentEvent?.classesOrGroups
          .map((c) => {
            return c.__typename === 'Class' ? c.uuid : '';
          })
          .filter((c) => c !== '') ?? [],
      groupUuids:
        currentEvent?.classesOrGroups
          .map((c) => {
            return c.__typename === 'Group' ? c.uuid : '';
          })
          .filter((c) => c !== '') ?? [],
      subjectUuid: currentEvent?.subject.uuid ?? '',
      teacherUuids: currentEvent?.personsConnection.edges.map((e) => e.node.uuid) ?? [],
      startDate: currentEvent?.start ? dayjs(currentEvent.start) : dayjs().startOf('day'),
      startTime: currentEvent?.start ? dayjs(currentEvent.start) : dayjs().startOf('day'),
      endDate: currentEvent?.end ? dayjs(currentEvent.end) : dayjs().endOf('day'),
      endTime: currentEvent?.end ? dayjs(currentEvent?.end) : dayjs().endOf('day'),
      isClassbookRelevant: currentEvent?.isClassbookRelevant ?? false,
      rrule: null,
      excludeHolidays: true,
    };
  }, [
    currentEvent?.classesOrGroups,
    currentEvent?.comment,
    currentEvent?.end,
    currentEvent?.isClassbookRelevant,
    currentEvent?.personsConnection.edges,
    currentEvent?.start,
    currentEvent?.subject.uuid,
    currentEvent?.uuid,
  ]);

  const { classesData, groupsData, divisionsData, teacherData, subjectData } = useLoadBasicData({ pause: false });

  const activeTeachers = teacherData?.people.filter((p) => p.active);

  const classesOptions = useCreateSelectOptions(classesData?.classes, 'uuid', 'name');
  const subjectOptions = useCreateSelectOptions(subjectData?.subjects, 'uuid', 'name');
  const teacherOptions = useCreateSelectOptions(activeTeachers, 'uuid', 'listName');

  const filteredGroupOptions: SelectOptionType[] = useMemo(() => {
    const options: SelectOptionType[] = [];
    const classes = classesData?.classes.filter((c) => selectedClasses.includes(c.uuid));

    classes?.forEach((c) => {
      const divisions = divisionsData?.divisions.filter((d) => c.divisions.map((d) => d.uuid).includes(d.uuid));
      const groups = groupsData?.groups.filter((g) =>
        divisions?.flatMap((d) => d.groupsConnection.edges.map((gc) => gc.node.uuid)).includes(g.uuid),
      );
      groups?.forEach((g) => {
        options.push({
          label: `${g.name} (${c.name})`,
          value: g.uuid,
        });
      });
    });

    return options;
  }, [classesData?.classes, divisionsData?.divisions, groupsData?.groups, selectedClasses]);

  useEffect(() => {
    setSelectedClasses(initialValues.classUuids);
  }, [initialValues.classUuids]);

  async function handleSubmit(values: SchoolEventType, formHelpers: FormikHelpers<SchoolEventType>) {
    onLoading(true);

    if (!values.rrule) {
      if (!eventUuid) {
        const res = await createSingleEvent(
          {
            input: {
              comment: values.name,
              start: values.startDate.toISOString(),
              end: values.endDate.toISOString(),
              classes: values.classUuids,
              groups: values.groupUuids,
              rooms: [],
              subject: values.subjectUuid,
              isSchoolEvent: true,
              persons: values.teacherUuids,
              isClassbookRelevant: values.isClassbookRelevant,
            },
          },
          schoolEventContext,
        );
        if (res.error) {
          showUserErrorToast({ error: res.error });
        } else {
          showSuccessCreateToast();
        }
      }
    } else {
      if (!eventUuid) {
        const res = await createRecurringEvent(
          {
            input: {
              start: values.startDate.toISOString(),
              end: values.endDate.toISOString(),
              comment: values.name,
              classes: values.classUuids,
              groups: values.groupUuids,
              rooms: [],
              subject: values.subjectUuid,
              isClassbookRelevant: values.isClassbookRelevant,
              persons: values.teacherUuids,
              rrule: values.rrule,
              excludeHolidays: values.excludeHolidays,
            },
          },
          schoolEventContext,
        );
        if (res.error) {
          showUserErrorToast({ error: res.error });
        } else {
          showSuccessCreateToast();
        }
      }
    }

    onLoading(false);
    onClose();
  }

  return (
    <Formik<SchoolEventType>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schoolEventSchema}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, errors }) => {
        const allowRecurring = values.startDate.isSame(values.endDate, 'day');
        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={10}>
                  <GridRow spacingBottom='s'>
                    <Input
                      name='name'
                      value={values.name}
                      label={t('common.name')}
                      onChange={async (e) => await setFieldValue('name', e.target.value)}
                      error={errors.name}
                    />
                  </GridRow>
                  <GridRow spacingTop='s' spacingBottom='s'>
                    <GridColumn width={6}>
                      <Select
                        name='subject'
                        isClearable
                        error={errors.subjectUuid}
                        isSearchable
                        menuPosition='fixed'
                        label={t('subject.title', { count: 1 })}
                        options={subjectOptions}
                        value={subjectOptions?.find((s) => s.value === values.subjectUuid)}
                        onChange={async (option) => {
                          const opt = option as SingleValue<SelectOptionType>;
                          await setFieldValue('subjectUuid', opt?.value ?? null);
                        }}
                        required
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Select
                        name='teachers'
                        isMulti
                        isClearable
                        isSearchable
                        menuPosition='fixed'
                        label={t('persons.title', { count: 1 })}
                        options={teacherOptions}
                        value={teacherOptions?.filter((t) => values.teacherUuids.includes(t.value as string))}
                        onChange={async (options) => {
                          const opt = options as MultiValue<SelectOptionType>;
                          await setFieldValue(
                            'teacherUuids',
                            opt.map((o) => o.value),
                          );
                        }}
                        required
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <GridColumn width={6}>
                      <Select
                        name='classes'
                        isMulti
                        isSearchable
                        isClearable
                        menuPosition='fixed'
                        label={t('classes.title', { count: 1 })}
                        options={classesOptions}
                        value={classesOptions?.filter((c) => values.classUuids.includes(c.value as string))}
                        onChange={async (e) => {
                          const opt = e as MultiValue<SelectOptionType>;
                          await setFieldValue(
                            'classUuids',
                            opt.map((o) => o.value),
                          );
                          setSelectedClasses(opt.map((o) => o.value as string));
                        }}
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Select
                        name='groups'
                        isMulti
                        isSearchable
                        isClearable
                        menuPosition='fixed'
                        label={t('groups.title', { count: 1 })}
                        options={filteredGroupOptions}
                        value={filteredGroupOptions.filter((g) => values.groupUuids.includes(g.value as string))}
                        onChange={async (e) => {
                          const opt = e as MultiValue<SelectOptionType>;
                          await setFieldValue(
                            'groupUuids',
                            opt.map((o) => o.value),
                          );
                        }}
                        disabled={filteredGroupOptions.length === 0}
                        placeholder={lowercaseFirstLetter(t('schoolEvents.noClassesHint'))}
                      />
                    </GridColumn>
                  </GridRow>
                </GridColumn>
                <GridColumn width={2} align='end'>
                  <Checkbox
                    className='mt-3'
                    name='classbookRelevant'
                    label={t('schoolEvents.classbookRelevant')}
                    checked={values.isClassbookRelevant}
                    onChange={async (e) => await setFieldValue('isClassbookRelevant', e.target.checked)}
                  />
                </GridColumn>
              </GridRow>
              <GridRow headline={t('common.timeFrame')}>
                <GridColumn width={12}>
                  <GridRow spacingBottom='s'>
                    <GridColumn width={3}>
                      <DatePicker
                        disabled={!!values.rrule}
                        name='startDate'
                        value={values.startDate.toDate()}
                        label={t('common.from')}
                        onChange={async (e) => {
                          const newDate = dayjs(e);
                          const updated = values.startDate
                            .year(newDate.year())
                            .month(newDate.month())
                            .date(newDate.date());
                          await setFieldValue('startDate', updated);
                        }}
                        minDate={dayjs(currentSchoolYear?.start).toDate()}
                        maxDate={dayjs(currentSchoolYear?.end).toDate()}
                        error={errors.startDate as string}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='startTime'
                        label={t('common.start')}
                        value={values.startDate.format('HH:mm')}
                        onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                          const [hour, minute] = event.target.value.split(':').map(Number);
                          const updated = values.startDate.hour(hour).minute(minute);
                          await setFieldValue('startDate', updated);
                        }}
                        type='time'
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <DatePicker
                        disabled={!!values.rrule}
                        name='endDate'
                        value={values.endDate.toDate()}
                        label={t('common.until')}
                        onChange={async (e) => {
                          const newDate = dayjs(e);
                          const updated = values.endDate
                            .year(newDate.year())
                            .month(newDate.month())
                            .date(newDate.date());
                          await setFieldValue('endDate', updated);
                        }}
                        minDate={dayjs(currentSchoolYear?.start).toDate()}
                        maxDate={dayjs(currentSchoolYear?.end).toDate()}
                        error={errors.endDate as string}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        name='endTime'
                        label={t('common.end')}
                        value={dayjs(values.endDate).format('HH:mm')}
                        onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                          const [hour, minute] = event.target.value.split(':').map(Number);
                          const updated = values.startDate.hour(hour).minute(minute);
                          await setFieldValue('endDate', updated);
                        }}
                        type='time'
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <GridColumn width={12}>
                      <RecurringEventForm
                        key={`${values.startDate.toISOString()}_${values.endDate.toISOString()}`}
                        start={values.startDate.toDate()}
                        rrule={values.rrule}
                        excludeHolidays={values.excludeHolidays}
                        onExcludeHolidaysChange={(value) => {
                          setFieldValue('excludeHolidays', value);
                        }}
                        onChange={(rrule) => {
                          setFieldValue('rrule', rrule);
                        }}
                        allowRecurring={allowRecurring}
                        isRecurrenceAllowed={(frequency) => {
                          if (!frequency) {
                            return false;
                          }
                          if (values.startDate.isSame(values.endDate, 'day')) {
                            return true;
                          }
                          const daysDiff = getDaysDifference(values.startDate, values.endDate);
                          if (frequency >= 3) {
                            return daysDiff < 7;
                          }
                          if (frequency === 2) {
                            return daysDiff < 7;
                          }
                          if (frequency === 1) {
                            return daysDiff < 31;
                          }
                          return false;
                        }}
                      />
                    </GridColumn>
                  </GridRow>
                </GridColumn>
              </GridRow>
            </Grid>
            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  onClose();
                  resetForm();
                },
              }}
              submitButton={{
                disabled: isSubmitting || !dirty,
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
